import { formatInstant } from "@/lib/time/displayTime";
/**
 * #927 follow-up (owner, QA 2026-10-13): how the connection editor words a validation answer.
 *
 * The connector hub writes the answer, and two things in it read badly next to our own words: a
 * message with no full stop runs into the sentence after it ("…password You can still save"), and
 * a list of missing fields names the seed's keys ("serverName, databaseName") instead of the
 * labels the form shows. Both are fixed here, in the client, for the card above the form and the
 * line beside the bottom Validate button alike.
 */

/**
 * #952 review: the validation cards at full strength, semantic tokens only. skypoint-ui's message
 * bar is a pale tint with a faint border; an answer to Validate is read at a glance, so its border
 * is the full colour and the failed card's title is in the error colour.
 */
export const VALIDATION_FAILED_CARD = "border-destructive [&_p.font-semibold]:text-destructive";
export const VALIDATION_PASSED_CARD = "border-success";

/** A field's key and the label the form shows for it. */
export interface FieldLabel {
  id: string;
  name: string;
}

/** `text` ending in `.`, `!` or `?`: a full stop added only when it has none. */
export function withFinalStop(text: string): string {
  const trimmed = text.trim();
  if (trimmed === "") return trimmed;
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

/** Two sentences joined so the first always ends before the second begins. */
export function joinSentences(first: string, second: string): string {
  const head = withFinalStop(first);
  return head === "" ? second : `${head} ${second}`;
}

/** Sentences joined in order, each ended with a full stop, the empty ones left out. */
export function joinParts(...parts: readonly string[]): string {
  return parts
    .map(withFinalStop)
    .filter((part) => part !== "")
    .join(" ");
}

/**
 * "Last validated Oct 14, 3:10 PM IST." for a stored validation answer (#1220): the time in the
 * person's display zone, so an old answer is never read as current. Empty when it has no time.
 */
export function validatedAtSentence(
  at: string | null | undefined,
  zone: string,
  lead: "Last validated" | "Last validation failed",
): string {
  return at ? `${lead} ${formatInstant(at, zone, "short", { named: true })}.` : "";
}

/** The longest hub message `withFieldLabels` matches (#1411); a longer one is shown as it is. */
const MAX_LABELLED_MESSAGE = 2_000;

/** The pieces of a hub message that names fields: text up to the list, the list, its stop. */
export interface FieldListMatch {
  lead: string;
  list: string;
  stop: string;
}

/** Whether a "field:" or "fields:" lead, with a word boundary before it, ends at the colon `colon`. */
const isLeadColon = (text: string, colon: number): boolean => {
  const plural = text.slice(Math.max(0, colon - 6), colon).toLowerCase();
  const word = plural.endsWith("fields") ? 6 : plural.endsWith("field") ? 5 : 0;
  if (word === 0) return false;
  const before = colon - word - 1;
  return before < 0 || !/\w/.test(text[before] ?? "");
};
const isStop = (char: string | undefined): boolean => char === "." || char === "!" || char === "?";
/** Whether the character at `at` is white space, as `\s` reads it; ASCII is decided without a call. */
const isSpaceAt = (text: string, at: number): boolean => {
  const code = text.charCodeAt(at);
  if (code === 32 || (code >= 9 && code <= 13)) return true;
  return code > 127 && (text[at] ?? "x").trim() === "";
};

/**
 * Splits "Missing required fields: serverName, userName." at its LAST "field(s):" lead that has a
 * list after it (the earlier leads are tried only when a later one has none). Time is bounded by
 * one backward scan for the leads, one for the last stop, and one skip over the spaces after each lead
 * (each bounded by the gap to the next lead): no regex backtracks over the text, so it is linear
 * in the message whatever the message holds (#1411). Reads the text up to its first line break,
 * as the pattern it replaces did. Null when there is no list to label.
 */
export function matchFieldList(message: string): FieldListMatch | null {
  const length = message.length;
  const eol = message.search(/[\n\r\u2028\u2029]/);
  const limit = eol === -1 ? length : eol;
  let content = length;
  while (content > 0 && isSpaceAt(message, content - 1)) content--;
  const stop = isStop(message[content - 1]) ? (message[content - 1] ?? "") : "";
  const listEnd = stop === "" ? content : content - 1;
  let lastStop = listEnd - 1;
  while (lastStop >= 0 && !isStop(message[lastStop])) lastStop--;
  let colon = limit > 0 ? message.lastIndexOf(":", limit - 1) : -1;
  for (; colon >= 0; colon = colon > 0 ? message.lastIndexOf(":", colon - 1) : -1) {
    if (!isLeadColon(message, colon)) continue;
    const leadEnd = colon + 1;
    if (leadEnd >= length) continue;
    let start = leadEnd;
    while (start < length && isSpaceAt(message, start)) start++;
    if (start < listEnd && lastStop < start) {
      return { lead: message.slice(0, start), list: message.slice(start, listEnd).trimEnd(), stop };
    }
    // Spaces and nothing else before the stop or the end: no key to label, shown as written.
    if (start > leadEnd && start >= listEnd && (stop !== "" || start === length)) {
      return { lead: stop === "" ? message.slice(0, -1) : message, list: "", stop: "" };
    }
  }
  return null;
}

/**
 * The hub's "Missing required fields: serverName, userName" with each key replaced by the label
 * the form shows ("Server name, User name"). A key the connector does not declare stays as it is.
 * Any other message is returned unchanged.
 */
export function withFieldLabels(message: string, fields: readonly FieldLabel[]): string {
  // #1411: the hub's text is stored with no length cap. A message past MAX_LABELLED_MESSAGE is
  // never matched and never cut. The match itself is linear (see matchFieldList); the cap is a
  // second bound, not the one the time depends on.
  if (message.length > MAX_LABELLED_MESSAGE) return message;
  const match = matchFieldList(message);
  if (!match) return message;
  const { lead, list, stop } = match;
  const labels = new Map(fields.map((field) => [field.id, field.name]));
  const named = list
    .split(",")
    .map((key) => key.trim())
    .filter((key) => key !== "")
    .map((key) => labels.get(key)?.trim() || key)
    .join(", ");
  return `${lead}${named}${stop}`;
}

/** The hub's message as the editor shows it: labels for keys, and a full stop at its end. */
export function validationSentence(message: string, fields: readonly FieldLabel[]): string {
  return withFinalStop(withFieldLabels(message, fields));
}

/**
 * Scrolls an element into view and moves focus to it, without a second jump. A reader who asked
 * for reduced motion gets the jump without the animation. jsdom has neither `scrollIntoView` nor
 * `matchMedia`; a browser always has both.
 */
export function reveal(element: HTMLElement | null): void {
  if (!element) return;
  element.scrollIntoView?.({ block: "start", behavior: scrollBehaviour() });
  element.focus({ preventScroll: true });
}

/**
 * #952 review: an invalid field is brought to the middle of the view by its whole control (the
 * label, the input or the visible button of a file field, and its error), never by a bare input
 * scrolled to the top edge under the sticky header; then the input takes focus without a second
 * jump.
 */
export function revealField(input: HTMLElement | null): void {
  if (!input) return;
  const control =
    input.closest<HTMLElement>('[data-testid^="field-"]') ??
    input.closest<HTMLElement>("[data-testid]") ??
    input;
  control.scrollIntoView?.({ block: "center", behavior: scrollBehaviour() });
  input.focus({ preventScroll: true });
}

function scrollBehaviour(): ScrollBehavior {
  const reduceMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return reduceMotion ? "auto" : "smooth";
}
